import type { UseMutationResult } from '@tanstack/react-query';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { GooglePlaceDto, Location } from '@up2stay/up2promo-api-client';
import { IntegrationsApi } from '@up2stay/up2promo-api-client';
import { Button, cn } from '@up2stay/up2promo-ui';
import { useState } from 'react';
import { toast } from 'sonner';

import { FeatureGate } from '@/core/components/authz/feature-gate';
import { ReputationConnectAllowance } from '@/core/components/billing/quota-indicators';
import { UpgradeCard } from '@/core/components/billing/upgrade-surfaces';
import { ConnectReviewProviderDialog } from '@/core/components/integrations/connect-review-provider-dialog';
import { LocationSearchCombobox } from '@/core/components/locations/location-search-combobox';
import { ShellLink } from '@/core/components/navigation/shell-link';
import {
  SettingsGroupRow,
  SettingsGroupRows,
} from '@/core/components/settings/settings-group';
import { SettingsSection } from '@/core/components/settings/settings-section';
import { useNavigateShell } from '@/core/hooks/use-navigate-shell';
import { useEntitlements } from '@/core/hooks/use-entitlements';
import { useIsMobile } from '@/core/hooks/use-mobile';
import { useChannelLocations } from '@/core/hooks/use-reputation';
import {
  getLocationConnectionStatus,
  getReviewListingId,
  GOOGLE_REVIEW_PROVIDER,
} from '@/core/lib/review-channel-setup';
import { useDictionary } from '@/core/providers/dictionary-provider';
import { shellPaths } from '@/core/router/app-paths';
import { locationsQueryKeys } from '@/core/query-keys/locations-query-keys';
import { integrationsQueryKeys } from '@/core/query-keys/integrations-query-keys';
import { reputationQueryKeys } from '@/core/query-keys/reputation-query-keys';

import type { LocationDetailDict } from './location-detail-types';
import { GoogleConnectionBadge } from './location-health-indicators';

export function LocationDetailIntegrationsTab({
  location,
  workspaceId,
  googleConnected,
  updateListingMutation,
  clearListingMutation,
  dict,
}: {
  location: Location | null;
  workspaceId: string;
  googleConnected: boolean;
  updateListingMutation: UseMutationResult<
    void,
    Error,
    GooglePlaceDto,
    unknown
  >;
  clearListingMutation: UseMutationResult<void, Error, void, unknown>;
  dict?: LocationDetailDict;
}) {
  const { navigateShell } = useNavigateShell();
  const { isFeatureEnabled } = useEntitlements();
  const queryClient = useQueryClient();
  const globalDict = useDictionary();
  const isMobile = useIsMobile();
  const d = dict?.integrations;
  const gp = d?.googlePlaces;
  const [connectOpen, setConnectOpen] = useState(false);
  const [editingListing, setEditingListing] = useState(false);

  const { data: channelLocations = [] } = useChannelLocations(
    workspaceId,
    {
      locationId: location?.id,
    },
    { enabled: isFeatureEnabled('feature.reputation') },
  );

  const googleStatus = location
    ? getLocationConnectionStatus(
        location,
        channelLocations,
        GOOGLE_REVIEW_PROVIDER,
      )
    : 'needs_action';

  const listingId = location
    ? getReviewListingId(location, GOOGLE_REVIEW_PROVIDER)
    : null;
  const syncLinked = googleStatus === 'linked';
  const listingLockedBySync = syncLinked;

  const unlinkMutation = useMutation({
    mutationFn: async () => {
      if (!location) return;
      await IntegrationsApi.unlink({
        path: { workspaceId, locationId: location.id },
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: integrationsQueryKeys.root,
      });
      void queryClient.invalidateQueries({
        queryKey: reputationQueryKeys.root,
      });
      toast.success(gp?.unlinkSuccess);
    },
    onError: () => {
      toast.error(gp?.unlinkError);
    },
  });

  const clearSkipMutation = useMutation({
    mutationFn: async () => {
      if (!location) return;
      await IntegrationsApi.clearSkip({
        path: { workspaceId, locationId: location.id },
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: integrationsQueryKeys.root,
      });
      void queryClient.invalidateQueries({
        queryKey: reputationQueryKeys.root,
      });
      void queryClient.invalidateQueries({ queryKey: locationsQueryKeys.root });
      setEditingListing(true);
    },
    onError: () => {
      toast.error(gp?.clearSkipError);
    },
  });

  const skipMutation = useMutation({
    mutationFn: async () => {
      if (!location) return;
      await IntegrationsApi.skip({
        path: { workspaceId, locationId: location.id },
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: integrationsQueryKeys.root,
      });
      void queryClient.invalidateQueries({
        queryKey: reputationQueryKeys.root,
      });
      void queryClient.invalidateQueries({ queryKey: locationsQueryKeys.root });
      toast.success(globalDict.reputation?.setup?.connectDialog?.skipSuccess);
    },
    onError: () => {
      toast.error(globalDict.reputation?.setup?.connectDialog?.skipError);
    },
  });

  const showListingSearch =
    googleStatus !== 'opt_out' &&
    (!listingId || editingListing) &&
    !listingLockedBySync;

  const listingTitle = gp?.title ?? d?.title ?? 'Google';
  const listingFooter = gp?.description ?? d?.description;
  const syncTitle = gp?.syncSectionTitle ?? 'Review sync';
  const syncFooter = !googleConnected
    ? gp?.syncOptionalHint
    : gp?.syncSectionDescription;

  const syncActions = googleConnected ? (
    <FeatureGate
      feature="feature.reputation"
      fallback={<UpgradeCard feature="feature.reputation" compact />}
    >
      <div
        className={cn(
          'flex flex-wrap items-center gap-2',
          isMobile ? 'w-full' : 'shrink-0',
        )}
      >
        {syncLinked ? (
          <Button
            variant="outline"
            size="sm"
            className={isMobile ? 'w-full' : undefined}
            onClick={() => unlinkMutation.mutate()}
            disabled={unlinkMutation.isPending}
          >
            {gp?.unlink ?? gp?.disconnect}
          </Button>
        ) : (
          <Button
            variant="primary"
            size="sm"
            className={isMobile ? 'w-full' : undefined}
            onClick={() => setConnectOpen(true)}
          >
            {gp?.enableSync ?? gp?.link}
          </Button>
        )}
      </div>
    </FeatureGate>
  ) : (
    <Button
      variant="outline"
      size="sm"
      className={isMobile ? 'w-full shrink-0' : 'shrink-0'}
      onClick={() => navigateShell(shellPaths.moreWorkspaceIntegrations)}
    >
      {gp?.connectWorkspace ?? gp?.goToIntegrations}
    </Button>
  );

  return (
    <FeatureGate
      feature="feature.reputation"
      fallback={<UpgradeCard feature="feature.reputation" compact />}
    >
      <div className="flex min-w-0 w-full flex-col gap-6">
        <SettingsSection
          id="location-google-listing"
          title={listingTitle}
          footer={listingFooter}
          info={gp?.listingSectionDescription}
          grouped
          actions={
            <GoogleConnectionBadge
              status={googleStatus}
              className="shrink-0"
              labels={{
                googleLinked: gp?.syncedLabel ?? gp?.linkedLabel ?? 'Synced',
                googleListing: gp?.listingLabel ?? 'Listing only',
                googleNeedsAction: gp?.needsActionLabel ?? 'Needs action',
                googleOptOut: gp?.optOutLabel ?? 'Not using Google',
              }}
            />
          }
        >
          <SettingsGroupRows>
            {googleStatus === 'opt_out' ? (
              <SettingsGroupRow>
                <div
                  className={cn(
                    'flex gap-3',
                    isMobile
                      ? 'flex-col'
                      : 'flex-row items-center justify-between',
                  )}
                >
                  <p className="text-[13.5px] leading-relaxed text-muted-foreground text-pretty">
                    {gp?.optOutHint}
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    className={isMobile ? 'w-full shrink-0' : 'shrink-0'}
                    onClick={() => clearSkipMutation.mutate()}
                    disabled={clearSkipMutation.isPending}
                  >
                    {gp?.connectAnyway}
                  </Button>
                </div>
              </SettingsGroupRow>
            ) : null}

            {googleStatus === 'needs_action' && !listingId ? (
              <SettingsGroupRow>
                <div className="flex flex-col gap-3">
                  <p className="text-[13.5px] font-medium leading-relaxed text-foreground">
                    {gp?.listingSectionTitle ?? 'Link a Google listing'}
                  </p>
                  <LocationSearchCombobox
                    placeholder={
                      gp?.listingSearchPlaceholder ?? gp?.searchPlaceholder
                    }
                    establishmentPoiOnly
                    disabled={updateListingMutation.isPending}
                    onSelect={(place) => {
                      updateListingMutation.mutate(place, {
                        onSuccess: () => {
                          setEditingListing(false);
                          toast.success(gp?.listingLinkSuccess);
                        },
                      });
                    }}
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    className={isMobile ? 'w-full' : 'w-fit'}
                    onClick={() => skipMutation.mutate()}
                    disabled={skipMutation.isPending}
                  >
                    {gp?.notUsingGoogle}
                  </Button>
                </div>
              </SettingsGroupRow>
            ) : null}

            {listingId && !editingListing ? (
              <SettingsGroupRow>
                <div
                  className={cn(
                    'flex min-w-0 gap-3',
                    isMobile
                      ? 'flex-col'
                      : 'flex-row items-center justify-between',
                  )}
                >
                  <p className="min-w-0 truncate text-[13.5px] leading-relaxed text-muted-foreground">
                    <span className="font-medium text-foreground">
                      {gp?.listingLabel ?? 'Listing'}
                    </span>
                    <span className="mx-1.5 text-border">·</span>
                    <span className="font-mono text-xs opacity-80">
                      {listingId.length > 18
                        ? `${listingId.slice(0, 10)}…${listingId.slice(-6)}`
                        : listingId}
                    </span>
                  </p>
                  <div
                    className={cn(
                      'flex flex-wrap gap-2',
                      isMobile ? 'w-full' : 'shrink-0',
                    )}
                  >
                    <Button
                      variant="outline"
                      size="sm"
                      className={isMobile ? 'flex-1' : undefined}
                      disabled={listingLockedBySync}
                      title={
                        listingLockedBySync
                          ? (gp?.unlinkSyncBeforeChange ?? undefined)
                          : undefined
                      }
                      onClick={() => setEditingListing(true)}
                    >
                      {gp?.changeListing ?? 'Change'}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className={isMobile ? 'flex-1' : undefined}
                      disabled={
                        listingLockedBySync || clearListingMutation.isPending
                      }
                      title={
                        listingLockedBySync
                          ? (gp?.unlinkSyncBeforeChange ?? undefined)
                          : undefined
                      }
                      onClick={() => clearListingMutation.mutate()}
                    >
                      {gp?.clearListing ?? 'Remove'}
                    </Button>
                  </div>
                </div>
                {listingLockedBySync ? (
                  <p className="pt-2 text-[12px] leading-snug text-muted-foreground/85 text-pretty">
                    {gp?.unlinkSyncBeforeChange}
                  </p>
                ) : null}
              </SettingsGroupRow>
            ) : null}

            {showListingSearch &&
            !(googleStatus === 'needs_action' && !listingId) ? (
              <SettingsGroupRow separated={Boolean(listingId)}>
                <div className="flex flex-col gap-2">
                  <LocationSearchCombobox
                    placeholder={
                      gp?.listingSearchPlaceholder ?? gp?.searchPlaceholder
                    }
                    establishmentPoiOnly
                    disabled={updateListingMutation.isPending}
                    onSelect={(place) => {
                      updateListingMutation.mutate(place, {
                        onSuccess: () => {
                          setEditingListing(false);
                          toast.success(gp?.listingLinkSuccess);
                        },
                      });
                    }}
                  />
                  {editingListing && listingId ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      className={isMobile ? 'w-full' : 'w-fit'}
                      onClick={() => setEditingListing(false)}
                    >
                      {gp?.cancelEditListing ?? 'Cancel'}
                    </Button>
                  ) : null}
                </div>
              </SettingsGroupRow>
            ) : null}

            {googleStatus === 'needs_action' && listingId ? (
              <SettingsGroupRow separated>
                <Button
                  variant="outline"
                  size="sm"
                  className={isMobile ? 'w-full' : 'w-fit'}
                  onClick={() => skipMutation.mutate()}
                  disabled={skipMutation.isPending}
                >
                  {gp?.notUsingGoogle}
                </Button>
              </SettingsGroupRow>
            ) : null}
          </SettingsGroupRows>
        </SettingsSection>

        {googleStatus !== 'opt_out' ? (
          <SettingsSection
            id="location-review-sync"
            title={syncTitle}
            footer={syncFooter}
            grouped
          >
            <SettingsGroupRows>
              <SettingsGroupRow>
                <div
                  className={cn(
                    'flex gap-3',
                    isMobile
                      ? 'flex-col'
                      : 'flex-row items-center justify-between',
                  )}
                >
                  {syncActions}
                </div>
                {googleConnected ? (
                  <div className="pt-3">
                    <ReputationConnectAllowance />
                  </div>
                ) : null}
              </SettingsGroupRow>
            </SettingsGroupRows>
          </SettingsSection>
        ) : null}

        {d?.setupBanner ? (
          <p className="px-1 text-[12px] leading-snug text-muted-foreground/85">
            {d.setupBanner}{' '}
            <ShellLink
              to={`${shellPaths.reputationSetup}?tab=connections`}
              className="text-foreground underline-offset-4 hover:underline"
            >
              {d.setupBannerCta}
            </ShellLink>
          </p>
        ) : null}

        {location && (
          <ConnectReviewProviderDialog
            open={connectOpen}
            onOpenChange={setConnectOpen}
            workspaceId={workspaceId}
            location={location}
            provider={GOOGLE_REVIEW_PROVIDER}
          />
        )}
      </div>
    </FeatureGate>
  );
}
